import { Injectable } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { BehaviorSubject, catchError, debounceTime, EMPTY, from, Subject, switchMap, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class UserGroupService {
    private userGroupSubject = new BehaviorSubject<UserGroupGet | null>(null);

    public currentUserGroup$ = this.userGroupSubject.asObservable();

    private getUserGroupSubject = new Subject<GetUserGroupParams | null>();

    constructor(private datatask: DataTaskService) {
        this.getUserGroupSubject
            .pipe(
                debounceTime(150),
                switchMap((value) => {
                    if (!!value) {
                        return from(this.getUserGroup(value)).pipe(catchError(() => of(null)));
                    } else {
                        return of(null);
                    }
                })
            )
            .subscribe({ next: (value) => this.userGroupSubject.next(value) });
    }

    public setCurrentUserGroup(params: GetUserGroupParams | null) {
        this.getUserGroupSubject.next(params);
    }

    /**
     * Retrieves user-group for the given params.
     *
     * @datatask 99
     * @datataskName sp_ws_p2_usergroup_get
     * @param params usergroup keyno.
     * @returns promise containing the user group.
     */
    public async getUserGroup(params: GetUserGroupParams): Promise<UserGroupGet> {
        return (await this.datatask.Post(99, params))[0];
    }

    /**
     * Saves the given usergroup, either update or add.
     *
     * @datatask 100
     * @datataskName sp_ws_p2_usergroup_save
     * @param params the get to save.
     * @returns returns an empty promise which resolves when the save has completed.
     */
    public async saveUserGroup(params: UserGroupGet): Promise<{ usergroup_keyno: `${number}` | number }> {
        return (await this.datatask.Post(100, params))[0];
    }

    /**
     * Deletes the given usergroup.
     *
     * @datatask
     * @datataskName tt_usergroup_delete
     * @param params the user group to delete.
     * @returns empty promise which resolves when the delete has completed.
     */
    public async deleteUserGroup(params: UserGroupGet): Promise<void> {
        try {
            const response = await this.datatask.Post(3830, params);

            if (!!response && typeof response === 'object' && 'validation' in response && !!response.validation) throw Error(response.message);

            this.setCurrentUserGroup(null);
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
        }
    }
}

export interface UserGroupGet {
    heading: string;
    usergroup_keyno: `${number}` | number;
    usergroup_name: string;
    dba_rights: '1' | '0';
    show_delete: '1' | '0';
    is_terminal_group: '1' | '0';
}

export interface GetUserGroupParams {
    usergroup_keyno: `${number}` | number;
}
